import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";

import { SignUpComponent } from "./components/signUp/SignUpComponent";
import { SignInComponent } from "./components/signIn/SignInComponent";
import { DashboardComponent } from "./components/dashboard/DashboardComponent";
import { FooterComponent } from "./components/common/footer/FooterComponent";

import "./App.scss";
import ProtectedRoute from "./utils/protectedRouts";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CircularProgress, Box } from "@mui/material";
import RoleGuard from "./utils/roleGuard";

// Lazy-loaded page components for code splitting
const HomeComponent = lazy(() => import("./components/home/HomeComponent").then(m => ({ default: m.HomeComponent })));
const UpcomingLabTestComponent = lazy(() => import("./components/lab/upcomingTest/UpcomingLabTestComponent").then(m => ({ default: m.UpcomingLabTestComponent })));
const MemberComponent = lazy(() => import("./components/lab/member/MemberComponent").then(m => ({ default: m.MemberComponent })));
const MemberUpdateComponent = lazy(() => import("./components/lab/member/MemberUpdateComponent").then(m => ({ default: m.MemberUpdateComponent })));
const TestDetailsComponent = lazy(() => import("./components/lab/upcomingTest/TestDetailsComponent"));
const CustomersPage = lazy(() => import("./components/customers/CustomersPage"));
const TestsPage = lazy(() => import("./components/tests/TestsPage"));
const TestParametersPage = lazy(() => import("./components/tests/TestParametersPage"));
const CheckupsPage = lazy(() => import("./components/checkups/CheckupsPage"));
const AppointmentsPage = lazy(() => import("./components/appointments/AppointmentsPage"));
const ReportsPage = lazy(() => import("./components/reports/ReportsPage"));
const PaymentsPage = lazy(() => import("./components/payments/PaymentsPage"));
const SubscriptionsPage = lazy(() => import("./components/subscriptions/SubscriptionsPage"));
const NotificationsPage = lazy(() => import("./components/notifications/NotificationsPage"));
const ContentPage = lazy(() => import("./components/content/ContentPage"));
const OffersPage = lazy(() => import("./components/offers/OffersPage"));
const AnalyticsPage = lazy(() => import("./components/analytics/AnalyticsPage"));
const UsersPage = lazy(() => import("./components/users/UsersPage"));
const ProfilePage = lazy(() => import("./components/profile/ProfilePage"));
const OcrReviewPage = lazy(() => import("./components/ocr/OcrReviewPage"));
const HealthScoresPage = lazy(() => import("./components/healthScores/HealthScoresPage"));

const theme = createTheme({
  typography: {
    fontFamily: ["Montserrat", "sans-serif"].join(","),
  },
  palette: {
    primary: { main: "#26285d" },
    secondary: { main: "#61aa9d" },
  },
  colorSchemes: {
    light: true,
    dark: false,
  },
});

function App() {
  return (
    <div className="wary-app">
      <ThemeProvider theme={theme}>
        <Router>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/signup" element={<><SignUpComponent /><FooterComponent variant="public" /></>} />
            <Route path="/signin" element={<><SignInComponent /><FooterComponent variant="public" /></>} />
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<DashboardComponent />}>
                <Route index element={<HomeComponent />} />
                {/* Phase 2: Upcoming Tests routes — lab scheduling hidden for Phase 1 */}
                {/* <Route
                  path="upcoming-tests"
                  element={<UpcomingLabTestComponent />}
                />
                <Route
                  path="upcoming-tests/:testId"
                  element={<TestDetailsComponent />}
                /> */}
                {/* Phase 2: Lab Members routes — hidden for Phase 1 */}
                {/* <Route path="lab-members" element={<MemberComponent />} />
                <Route
                  path="lab-members/add-member"
                  element={<MemberUpdateComponent />}
                /> */}
                <Route path="customers" element={<CustomersPage />} />
                {/* Phase 2: Tests routes — lab test catalog hidden for Phase 1 */}
                {/* <Route path="tests" element={<TestsPage />} />
                <Route
                  path="tests/:testId/parameters"
                  element={<TestParametersPage />}
                /> */}
                {/* Phase 2: Checkups (Packages) route — hidden for Phase 1 */}
                {/* <Route path="checkups" element={<CheckupsPage />} /> */}
                {/* Phase 2: Appointments route — hidden for Phase 1 */}
                {/* <Route path="appointments" element={<AppointmentsPage />} /> */}
                <Route path="reports" element={<ReportsPage />} />
                <Route path="payments" element={<PaymentsPage />} />
                <Route path="subscriptions" element={<SubscriptionsPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="content" element={<ContentPage />} />
                <Route path="offers" element={<OffersPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="ocr" element={<OcrReviewPage />} />
                <Route path="health-scores" element={<HealthScoresPage />} />
                <Route element={<RoleGuard allowedRoles={['superAdmin']} />}>
                  <Route path="analytics" element={<AnalyticsPage />} />
                  <Route path="users" element={<UsersPage />} />
                </Route>
              </Route>
            </Route>
            {/* Unknown URLs land on the dashboard; ProtectedRoute sends
                unauthenticated visitors on to /signin from there. */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </div>
  );
}

export { App };
